import { useCallback, useEffect, useRef, useState } from 'react';
import type { InstaQLResponse, ValidQuery } from '@instantdb/admin';
import type { AppSchema } from '../../instant.schema';
import { db } from './instant';
import { queryServer } from './server';
import { clearAccountSnapshots, readSnapshot, snapshotKey, writeSnapshot } from './confirmed-cache';

/** Realtime events invalidate an HTTP snapshot; optimistic data is never returned. */
export function useServerQuery<Q extends ValidQuery<Q, AppSchema>>(query: Q | null) {
  const { user } = db.useAuth();
  const signal = db.useQuery(query);
  const connection = db.useConnectionStatus();
  const appId = process.env.EXPO_PUBLIC_INSTANT_APP_ID ?? '';
  const key = snapshotKey(appId, user?.id ?? '', query);
  const previousUser = useRef(user?.id);
  const current = useRef({ key, query });
  current.current = { key, query };
  const sequence = useRef(0);
  const [snapshot, setSnapshot] = useState<{
    key: string;
    data?: InstaQLResponse<AppSchema, Q, false>;
    isLoading: boolean;
    error?: Error;
    fetchedAt?: number;
    isStale?: boolean;
  }>({ key, isLoading: !!query });

  const refresh = useCallback(async () => {
    const { key: requestKey, query: requestQuery } = current.current;
    const requestSequence = ++sequence.current;
    if (!requestQuery) {
      setSnapshot({ key: requestKey, isLoading: false });
      return;
    }
    let cached: ReturnType<typeof readSnapshot<InstaQLResponse<AppSchema, Q, false>>>;
    try { cached = readSnapshot(window.localStorage, requestKey); } catch { /* Private mode. */ }
    setSnapshot((previous) => previous.key === requestKey && previous.data
      ? { ...previous, isLoading: false }
      : { key: requestKey, ...cached, isStale: !!cached, isLoading: !cached });
    try {
      const data = await queryServer(requestQuery);
      if (requestSequence === sequence.current && requestKey === current.current.key) {
        const fetchedAt = Date.now();
        setSnapshot({ key: requestKey, data, fetchedAt, isLoading: false, isStale: false });
        try { writeSnapshot(window.localStorage, requestKey, { data, fetchedAt }); } catch { /* Private mode. */ }
      }
      return data;
    } catch (cause) {
      const error = cause instanceof Error ? cause : new Error('Unable to verify saved data.');
      if (requestSequence === sequence.current && requestKey === current.current.key) {
        setSnapshot((previous) => ({ ...previous, key: requestKey, isLoading: false, isStale: true, error }));
      }
      throw error;
    }
  }, []);

  useEffect(() => {
    if (previousUser.current && previousUser.current !== user?.id) {
      try { clearAccountSnapshots(window.localStorage, appId, previousUser.current); } catch { /* Private mode. */ }
    }
    previousUser.current = user?.id;
  }, [appId, user?.id]);

  useEffect(() => {
    void refresh().catch(() => {});
    return () => { sequence.current += 1; };
  }, [key, signal.data, connection, refresh]);

  useEffect(() => {
    const reload = () => { void refresh().catch(() => {}); };
    window.addEventListener('focus', reload);
    window.addEventListener('online', reload);
    return () => {
      sequence.current += 1;
      window.removeEventListener('focus', reload);
      window.removeEventListener('online', reload);
    };
  }, [refresh]);

  return {
    data: snapshot.key === key ? snapshot.data : undefined,
    error: snapshot.key === key ? snapshot.error : undefined,
    isLoading: !!query && (snapshot.key !== key || snapshot.isLoading),
    isStale: snapshot.key === key && !!snapshot.isStale,
    fetchedAt: snapshot.key === key ? snapshot.fetchedAt : undefined,
    refresh,
  };
}
